import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { Area, AreaChart, ResponsiveContainer } from 'recharts';

import { Alert, Loading } from '../../components';
import { Badge, Icon, ListingCard, ListingCardGrid, SearchInput, Segmented, Select } from '../../components/ui';
import { getFileUrl } from '../../core/config/api';
import { actionLabel, depthLabel, fmt, fmtDuration, isAway, localTime, placeLabel, sortListings } from './daoListingInsights';
import { initials, timeAgo } from './usageInsights';

import './dao-listings.scss';

// Usage Analytics → DAO Watches: every active DAO listing as its card, with how
// it does on daowatches.com. Front = views · time · people; flip = trend,
// countries, sources; the footer opens WHO looked. Backend:
// GET /api/admin/usage-analytics/dao-listings (retail-circle.com
// .claude/features/dao-listing-analytics.md).

const WINDOWS = [
  { value: '7', label: '7d' },
  { value: '30', label: '30d' },
  { value: '90', label: '90d' },
];

const SIGNALS = {
  lead: { label: 'Lead', tone: 'accent', hint: 'A signed-in buyer keeps coming back' },
  hot: { label: 'Hot', tone: 'warning', hint: 'Among the most viewed' },
  cold: { label: 'Cold', tone: 'info', hint: 'Listed 30+ days, barely seen' },
};

const SORTS = [
  { value: 'views', label: 'Most viewed' },
  { value: 'time', label: 'Longest look' },
  { value: 'people', label: 'Most signed-in' },
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest stock' },
];

const PAGE = 24;

function Spark({ data, label }) {
  const points = (data || []).map((v, i) => ({ i, v }));
  return (
    <div className="dla-spark" role="img" aria-label={label} title={label}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={points} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <Area type="monotone" dataKey="v" stroke="var(--accent)" fill="var(--accent-soft)" strokeWidth={1.5} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

function Chips({ icon, items }) {
  if (!items?.length) return null;
  return (
    <div className="dla-chips">
      <Icon name={icon} size="sm" />
      {items.map((c) => (
        <span key={c.name} className="dla-chip">{c.name} <b>{fmt(c.value)}</b></span>
      ))}
    </div>
  );
}

function BackFace({ l, days }) {
  return (
    <div className="dla-back">
      <div className="dla-back__stats">
        <div><strong>{fmt(l.views)}</strong><span>views</span></div>
        <div><strong>{fmt(l.visitors)}</strong><span>people</span></div>
        <div><strong>{fmtDuration(l.avg_seconds)}</strong><span>avg look</span></div>
      </div>
      <Spark data={l.daily} label={`Daily views, last ${days} days`} />
      <Chips icon="Globe" items={l.countries} />
      <Chips icon="ExternalLink" items={l.sources} />
      <Chips icon="MessageCircle" items={(l.actions || []).map((a) => ({ ...a, name: actionLabel(a.name) }))} />
      {!l.countries?.length && !l.sources?.length && <p className="dla-muted">No traffic yet</p>}
    </div>
  );
}

// 📍 London, UK · 🕑 14:32 their time — amber when away from their store's country.
function Where({ v }) {
  const place = placeLabel(v.city, v.country_code);
  const time = localTime(v.timezone);
  if (!place && !time) return null;
  return (
    <small className={`dla-viewers__meta${isAway(v) ? ' is-away' : ''}`}>
      {place && (
        <span title="Approximate, from their internet connection">
          <Icon name="MapPin" size="xs" /> {place}
        </span>
      )}
      {time && (
        <span title={`Their local time (${v.timezone})`}>
          <Icon name="Clock" size="xs" /> {time}
        </span>
      )}
      {isAway(v) && <span>store in {placeLabel(null, v.store_country_code)}</span>}
    </small>
  );
}

// 🖼 6/9 photos · 85% read, then the buttons they used.
function Depth({ v }) {
  const depth = depthLabel(v);
  if (!depth && !v.actions?.length) return null;
  return (
    <small className="dla-viewers__meta">
      {depth && <span><Icon name="Image" size="xs" /> {depth}</span>}
      {(v.actions || []).map((a) => <b key={a} className="dla-act">{actionLabel(a)}</b>)}
    </small>
  );
}

function Viewers({ viewers, onOpenUser }) {
  if (!viewers?.length) return <p className="dla-muted dla-viewers__empty">No signed-in viewers yet</p>;
  return (
    <ul className="dla-viewers" aria-label="Viewers">
      {viewers.map((v) => (
        <li key={v.user_id ? `u${v.user_id}` : `v${v.visitor}`} className={v.warm ? 'is-warm' : undefined}>
          <span className="dla-viewers__avatar" aria-hidden="true">{v.user_id ? initials(v.name) : <Icon name="User" size="xs" />}</span>
          <span className="dla-viewers__who">
            {v.user_id ? (
              <button type="button" className="dla-link" onClick={() => onOpenUser(v.user_id)}>{v.name || `User #${v.user_id}`}</button>
            ) : (
              <b>Visitor {v.visitor}</b>
            )}
            <small>{[v.store, v.via_ref && 'via link', v.device, v.source].filter(Boolean).join(' · ') || '—'}</small>
            <Where v={v} />
            <Depth v={v} />
            {v.also_viewed?.length > 0 && (
              <small className="dla-viewers__also" title="Their other DAO listings this period, most recent first">
                Also viewed {v.also_viewed.map((o) => o.label).join(', ')}
                {v.also_viewed_count > v.also_viewed.length && ` +${v.also_viewed_count - v.also_viewed.length}`}
              </small>
            )}
          </span>
          <span className="dla-viewers__nums" title={`Last seen ${timeAgo(v.last_seen)}`}>
            <b>{v.views}×</b>
            <small>{fmtDuration(v.seconds)}</small>
          </span>
        </li>
      ))}
    </ul>
  );
}

function DaoCard({ l, days, open, onToggle, onOpenUser }) {
  const signal = SIGNALS[l.signal];
  return (
    <ListingCard
      className="dla-card"
      brand={l.brand}
      reference={l.reference}
      model={l.model || '—'}
      price={l.price != null ? l.price / 100 : null}
      currency={l.currency}
      image={getFileUrl(l.image_id)}
      imageAlt={l.model}
      active
      reserved={l.reserved}
      marketplaceLabel="Who viewed"
      marketplacePanelAlign="end"
      marketplaceCount={fmt(l.signed_in)}
      marketplaceOpen={open}
      onMarketplaces={(e) => { e.stopPropagation(); onToggle(); }}
      onOpen={() => window.open(`https://www.daowatches.com/product/${l.id}`, '_blank', 'noopener')}
      slots={{
        infoLine: (
          <span className="dla-info">
            <span title="Views"><Icon name="Eye" size="xs" /> {fmt(l.views)}</span>
            <span title="Average time on page"><Icon name="Clock" size="xs" /> {fmtDuration(l.avg_seconds)}</span>
            {l.countries?.[0] && <span title="Top country"><Icon name="MapPin" size="xs" /> {l.countries[0].name}</span>}
          </span>
        ),
        photoOverlay: signal ? (
          <span className="dla-signal"><Badge tone={signal.tone} size="sm" title={signal.hint}>{signal.label}</Badge></span>
        ) : null,
        marketplaceAdornment: <Icon name="Users" size="xs" />,
        backFace: <BackFace l={l} days={days} />,
        marketplacePanel: open ? (
          <div className="dla-panel" onClick={(e) => e.stopPropagation()} role="presentation">
            <Viewers viewers={l.viewers} onOpenUser={onOpenUser} />
          </div>
        ) : null,
      }}
    />
  );
}

export default function DaoListingsTab() {
  const history = useHistory();
  const [days, setDays] = useState('30');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [signal, setSignal] = useState('all');
  const [sort, setSort] = useState('views');
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let live = true;
    setLoading(true);
    setError('');
    axios.get('/api/admin/usage-analytics/dao-listings', { params: { days } })
      .then((res) => live && setReport(res.data))
      .catch((e) => live && setError(e.response?.data?.message || e.message))
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [days]);

  // Any click outside an open viewers panel closes it.
  useEffect(() => {
    if (openId == null) return undefined;
    const close = () => setOpenId(null);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [openId]);

  useEffect(() => setShown(PAGE), [signal, sort, query, days]);

  const listings = useMemo(() => {
    const q = query.trim().toLowerCase();
    const list = (report?.listings || []).filter((l) =>
      (signal === 'all' || l.signal === signal)
      && (!q || [l.brand, l.model, l.reference, String(l.id)].some((v) => v && String(v).toLowerCase().includes(q))));
    return sortListings(list, sort);
  }, [report, signal, sort, query]);

  const s = report?.summary;
  const counts = s?.signals || {};
  const filters = [
    { value: 'all', label: `All ${fmt(s?.listings)}` },
    { value: 'lead', label: `Leads ${fmt(counts.lead)}` },
    { value: 'hot', label: `Hot ${fmt(counts.hot)}` },
    { value: 'cold', label: `Cold ${fmt(counts.cold)}` },
  ];

  return (
    <div className="dla">
      <div className="ua-toolbar">
        <Segmented label="Window" size="sm" options={WINDOWS} value={days} onChange={setDays} />
        <Segmented label="Show" size="sm" options={filters} value={signal} onChange={setSignal} />
        <div className="ua-toolbar__search">
          <SearchInput aria-label="Search listings" placeholder="Brand, model, reference" value={query} size="md" onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div className="dla-sort">
          <Select label="Sort" options={SORTS} value={sort} onChange={(v) => setSort(v || 'views')} />
        </div>
      </div>

      {error && <Alert type="error" message="Could not load DAO listing analytics" description={error} />}
      {loading && <Loading />}

      {!loading && report && (
        <>
          <div className="ua-tiles">
            <div className="ua-tile"><span>Views</span><strong>{fmt(s.views)}</strong></div>
            <div className="ua-tile"><span>People</span><strong>{fmt(s.visitors)}</strong></div>
            <div className="ua-tile"><span>Signed-in viewers</span><strong>{fmt(s.signed_in)}</strong></div>
            <div className="ua-tile"><span>Avg look</span><strong>{fmtDuration(s.avg_seconds)}</strong></div>
          </div>

          {listings.length === 0 ? (
            <p className="ua-empty">No listings match.</p>
          ) : (
            <ListingCardGrid minColumn={320} className="dla-grid">
              {listings.slice(0, shown).map((l) => (
                <DaoCard
                  key={l.id}
                  l={l}
                  days={days}
                  open={openId === l.id}
                  onToggle={() => setOpenId((id) => (id === l.id ? null : l.id))}
                  onOpenUser={(id) => history.push(`/usage-analytics/users/${id}`)}
                />
              ))}
            </ListingCardGrid>
          )}

          {listings.length > shown && (
            <button type="button" className="ua-chip dla-more" onClick={() => setShown((n) => n + PAGE)}>
              Show more · {fmt(listings.length - shown)}
            </button>
          )}

          <p className="ua-footnote">
            {report.ga?.status === 'ok' ? 'Google Analytics · daowatches.com only' : 'Google Analytics unavailable — site tracking only'}
            {report.tracking_since ? ` · viewers tracked since ${new Date(report.tracking_since).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : ''}
          </p>
        </>
      )}
    </div>
  );
}
